import { Button } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { Textarea } from '@chiroup/components/form/Textarea';
import {
  TRIAL_TOUR_MANIFESTS,
  TrialTourManifest,
  TrialTourStepManifest,
} from '@chiroup/core/types/TrialHome.type';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import appDataService from '../../services/app-data.service';
import Header from '../layout/Header';

/**
 * Marketing-facing editor for trial tour copy. Each step exposes title /
 * body / pro-tip / next-button-label as free text. Anchors, placement,
 * action vs info, and other engine knobs stay code-owned — marketing only
 * touches words. Saves an `AppData['trialTourOverrides']` blob keyed by
 * tour id, then step key. Empty/whitespace fields are stripped on save so
 * the override map only ever holds intentional changes.
 */

type StepOverride = {
  title?: string;
  body?: string;
  proTip?: string;
  nextLabel?: string;
};

type OverridesByTour = Record<string, Record<string, StepOverride>>;

const TrialToursEditor = () => {
  const [overrides, setOverrides] = useState<OverridesByTour>({});
  const [savedSnapshot, setSavedSnapshot] = useState<string>(JSON.stringify({}));
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let isDestroyed = false;

    const load = async () => {
      try {
        const res = await appDataService.get('trialTourOverrides');

        if (!isDestroyed && res) {
          setOverrides(res as OverridesByTour);
          setSavedSnapshot(JSON.stringify(res));
        }
      } catch {
        // No saved overrides yet — start blank.
      }
    };

    load();

    return () => {
      isDestroyed = true;
    };
  }, []);

  const isDirty = useMemo(
    () => JSON.stringify(overrides) !== savedSnapshot,
    [overrides, savedSnapshot]
  );

  const save = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const cleaned = pruneEmpty(overrides);
      await appDataService.update('trialTourOverrides', cleaned);
      setOverrides(cleaned);
      setSavedSnapshot(JSON.stringify(cleaned));
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const setStepField = (
    tourId: string,
    stepKey: string,
    field: keyof StepOverride,
    value: string
  ) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      const step = { ...(tour[stepKey] || {}) };
      // Empty string means "no override" — store as undefined so the code
      // default is used at render time.
      if (value.trim() === '') {
        delete step[field];
      } else {
        step[field] = value;
      }
      tour[stepKey] = step;

      return { ...prev, [tourId]: tour };
    });
  };

  const resetStep = (tourId: string, stepKey: string) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      delete tour[stepKey];

      return { ...prev, [tourId]: tour };
    });
  };

  return (
    <>
      <Header
        title="Trial Tours"
        rightSide={
          <div className="flex items-center gap-3">
            <DirtyStatePill isDirty={isDirty} saveMessage={saveMessage} />
            <Button text={isDirty ? 'Save changes' : 'Saved'} onClick={save} loading={isSaving} />
          </div>
        }
      />
      <div className="m-4 flex flex-col gap-4">
        <p className="text-sm text-gray-500 dark:text-dark-gray-400">
          Edit the copy shown to trial users during each in-app tour. Anchors and step order are
          wired in code — marketing only edits the words. Leave a field blank to use the code
          default.
        </p>

        {TRIAL_TOUR_MANIFESTS.map(tour => (
          <TourBlock
            key={tour.id}
            tour={tour}
            overrides={overrides[tour.id] || {}}
            onChangeField={(stepKey, field, value) => setStepField(tour.id, stepKey, field, value)}
            onResetStep={stepKey => resetStep(tour.id, stepKey)}
          />
        ))}
      </div>
    </>
  );
};

const DirtyStatePill: React.FC<{ isDirty: boolean; saveMessage: string }> = ({
  isDirty,
  saveMessage,
}) => {
  if (saveMessage === 'Saved!') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        <CheckCircleIcon className="h-4 w-4" />
        Saved
      </span>
    );
  }
  if (saveMessage === 'Error saving') {
    return <span className="text-xs font-medium text-red-600">Error saving</span>;
  }
  if (isDirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Unsaved changes
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      Up to date
    </span>
  );
};

const TourBlock: React.FC<{
  tour: TrialTourManifest;
  overrides: Record<string, StepOverride>;
  onChangeField: (stepKey: string, field: keyof StepOverride, value: string) => void;
  onResetStep: (stepKey: string) => void;
}> = ({ tour, overrides, onChangeField, onResetStep }) => (
  <div className="bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg p-5">
    <div className="flex items-center justify-between gap-3 mb-1">
      <div className="flex items-center gap-2 min-w-0">
        <h2 className="text-base font-semibold text-gray-900 dark:text-dark-gray-100 truncate">
          {tour.label}
        </h2>
        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 truncate">
          {tour.id}
        </span>
      </div>
    </div>
    {tour.hint && <p className="text-xs text-gray-500 dark:text-dark-gray-400 mb-4">{tour.hint}</p>}
    <div className="flex flex-col gap-4">
      {tour.steps.map((step, i) => (
        <StepBlock
          key={step.stepKey}
          stepNumber={i + 1}
          step={step}
          override={overrides[step.stepKey] || {}}
          onChangeField={(field, value) => onChangeField(step.stepKey, field, value)}
          onReset={() => onResetStep(step.stepKey)}
        />
      ))}
    </div>
  </div>
);

const StepBlock: React.FC<{
  stepNumber: number;
  step: TrialTourStepManifest;
  override: StepOverride;
  onChangeField: (field: keyof StepOverride, value: string) => void;
  onReset: () => void;
}> = ({ stepNumber, step, override, onChangeField, onReset }) => {
  const hasAnyOverride =
    !!override.title || !!override.body || !!override.proTip || !!override.nextLabel;

  return (
    <div className="border border-gray-100 dark:border-dark-gray-700 rounded-lg p-4 bg-gray-50/40 dark:bg-dark-gray-900/30">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400">
              Step {stepNumber}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 truncate">
              {step.stepKey}
            </span>
            {hasAnyOverride && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Edited
              </span>
            )}
          </div>
          {step.hint && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-gray-400">{step.hint}</p>
          )}
        </div>
        {hasAnyOverride && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 dark:border-dark-gray-600 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-xs font-medium text-gray-600 dark:text-dark-gray-300 transition-colors shrink-0"
          >
            <ArrowUturnLeftIcon className="h-3 w-3" />
            Reset to default
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <OverridableField
          label="Title"
          stepKey={step.stepKey}
          field="title"
          defaultValue={step.title}
          override={override.title}
          onChange={value => onChangeField('title', value)}
        />
        <OverridableField
          label="Body"
          stepKey={step.stepKey}
          field="body"
          defaultValue={step.body}
          override={override.body}
          onChange={value => onChangeField('body', value)}
          multiline
          rows={4}
          help="Plain text. Use blank lines for paragraph breaks. No formatting."
        />
        <OverridableField
          label="Pro tip (italic line under the body)"
          stepKey={step.stepKey}
          field="proTip"
          defaultValue={step.proTip}
          override={override.proTip}
          onChange={value => onChangeField('proTip', value)}
          multiline
          rows={2}
        />
        <OverridableField
          label="Next button label"
          stepKey={step.stepKey}
          field="nextLabel"
          defaultValue={step.nextLabel}
          override={override.nextLabel}
          onChange={value => onChangeField('nextLabel', value)}
          help={'Leave blank for the default ("Next" / "Finish").'}
        />
      </div>
    </div>
  );
};

const OverridableField: React.FC<{
  label: string;
  stepKey: string;
  field: keyof StepOverride;
  defaultValue?: string;
  override?: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
  help?: string;
}> = ({ label, stepKey, field, defaultValue, override, onChange, multiline, rows = 2, help }) => {
  const effective = override ?? defaultValue ?? '';
  const isOverridden = typeof override === 'string';
  const name = `${field}-${stepKey}`;
  const hint = help
    ? help
    : defaultValue
      ? `Default: "${defaultValue.replace(/\n+/g, ' / ')}"`
      : 'No code default — admin-only field.';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-700 dark:text-dark-gray-200">{label}</label>
        {isOverridden && (
          <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
            overriding default
          </span>
        )}
      </div>
      {multiline ? (
        <Textarea
          name={name}
          label=""
          value={effective}
          onChange={(v: string) => onChange(v)}
          rows={rows}
        />
      ) : (
        <Input name={name} label="" value={effective} onChange={(v: string) => onChange(v)} />
      )}
      <p className="mt-1 text-[10px] text-gray-400 dark:text-dark-gray-500 truncate">{hint}</p>
    </div>
  );
};

/**
 * Drop empty step records and empty tour records so the stored AppData blob
 * stays compact and `Object.keys` checks remain meaningful at render time.
 */
function pruneEmpty(overrides: OverridesByTour): OverridesByTour {
  const out: OverridesByTour = {};
  for (const [tourId, steps] of Object.entries(overrides)) {
    const cleanedSteps: Record<string, StepOverride> = {};
    for (const [stepKey, stepOverride] of Object.entries(steps)) {
      const nonEmpty = Object.fromEntries(
        Object.entries(stepOverride).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
      );
      if (Object.keys(nonEmpty).length > 0) cleanedSteps[stepKey] = nonEmpty as StepOverride;
    }
    if (Object.keys(cleanedSteps).length > 0) out[tourId] = cleanedSteps;
  }

  return out;
}

export default TrialToursEditor;
